import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Optional, Inject, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { serializeError } from 'serialize-error';
import * as i1 from '@angular/common/http';

var NGX_LOGGER_LEVELS;
(function (NGX_LOGGER_LEVELS) {
    NGX_LOGGER_LEVELS[NGX_LOGGER_LEVELS["DEBUG"] = 0] = "DEBUG";
    NGX_LOGGER_LEVELS[NGX_LOGGER_LEVELS["INFO"] = 1] = "INFO";
    NGX_LOGGER_LEVELS[NGX_LOGGER_LEVELS["WARN"] = 2] = "WARN";
    NGX_LOGGER_LEVELS[NGX_LOGGER_LEVELS["ERROR"] = 3] = "ERROR";
})(NGX_LOGGER_LEVELS || (NGX_LOGGER_LEVELS = {}));
var NGX_LITERAL_LOGGER_LEVELS;
(function (NGX_LITERAL_LOGGER_LEVELS) {
    NGX_LITERAL_LOGGER_LEVELS["DEBUG"] = "DEBUG";
    NGX_LITERAL_LOGGER_LEVELS["INFO"] = "INFO";
    NGX_LITERAL_LOGGER_LEVELS["WARN"] = "WARN";
    NGX_LITERAL_LOGGER_LEVELS["ERROR"] = "ERROR";
})(NGX_LITERAL_LOGGER_LEVELS || (NGX_LITERAL_LOGGER_LEVELS = {}));

/** The color tag that are gonna be replaced in the method */
const TO_REPLACE = '%COLOR%';
/** The base style */
const BASE_STYLE = [
    'background-color: ' + TO_REPLACE,
    'border-radius: 10%',
    'padding: 0.25em',
    'color: white',
    'font-weight: bold'
];

const STORAGE_SESSION_ID_KEY = 'ngxLoggerSessionId';

class NgxLoggerSessionIdService {
    constructor() { }
    /**
     * If he can get the session-id gets the value, if its not on the sessionStorage then
     * create and store the new session id
     * @return The session-id
     */
    getOrCreateSessionId() {
        if (this.sessionId) {
            return this.sessionId;
        }
        const sessionStorageId = sessionStorage.getItem(STORAGE_SESSION_ID_KEY);
        if (sessionStorageId) {
            return sessionStorageId;
        }
        else {
            const id = this.generate32BitString() + this.generate32BitString();
            sessionStorage.setItem(STORAGE_SESSION_ID_KEY, id);
            return id;
        }
    }
    refreshSessionId() {
        sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
        this.sessionId = null;
        this.getOrCreateSessionId();
    }
    /**
     * Generate a new 32 bit string
     * @return The 32 bit string
     */
    generate32BitString() {
        const array = new Uint32Array(1);
        // @ts-ignore: msCrypto its for IE
        const crypto = window.crypto || window.msCrypto;
        crypto.getRandomValues(array);
        return array[0].toString(16);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerSessionIdService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerSessionIdService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerSessionIdService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

const NGX_LOGGER_CONFIG = new InjectionToken('allianz.ngx-logger.config.token');
const DEFAULT_CONFIG = {
    console: {
        level: NGX_LOGGER_LEVELS.DEBUG,
        appendLevel: false,
        appendTimestamp: false,
    },
};
class NgxLoggerService {
    constructor(config, http, ngxLoggerSessionIdService) {
        this.config = config;
        this.http = http;
        this.ngxLoggerSessionIdService = ngxLoggerSessionIdService;
        this.debug = (msg, ...params) => this.logToConsole(NGX_LOGGER_LEVELS.DEBUG, msg, ...params);
        this.info = (msg, ...params) => this.logToConsole(NGX_LOGGER_LEVELS.INFO, msg, ...params);
        this.warn = (msg, ...params) => this.logToConsole(NGX_LOGGER_LEVELS.WARN, msg, ...params);
        this.error = (msg, ...params) => this.logToConsole(NGX_LOGGER_LEVELS.ERROR, msg, ...params);
        this.debugToServer = (msg, ...params) => this.log(NGX_LOGGER_LEVELS.DEBUG, msg, ...params);
        this.infoToServer = (msg, ...params) => this.log(NGX_LOGGER_LEVELS.INFO, msg, ...params);
        this.warnToServer = (msg, ...params) => this.log(NGX_LOGGER_LEVELS.WARN, msg, ...params);
        this.errorToServer = (msg, ...params) => this.log(NGX_LOGGER_LEVELS.ERROR, msg, ...params);
        if (!config) {
            this.config = DEFAULT_CONFIG;
        }
        else if (config.server && !http) {
            throw new Error('NgxLoggerService: HttpClient needs to be provided for server side logging');
        }
    }
    log(level, msg, ...params) {
        this.logToConsole(level, msg, ...params);
        try {
            this.logToServer(level, msg, ...params);
        }
        catch (error) {
            // We just ignore any errors with the server side logging, to not impact
            // the apps when the logging doesn't work
            console.error(error);
        }
    }
    logToConsole(level, msg, ...params) {
        if (this.config.console && level >= this.config.console.level) {
            let consoleMessage = msg;
            /** Literal in the console */
            const lvl = this.getConsoleMethodForLogLevel(level);
            // We add the hour in ISO format
            if (this.config.console.appendTimestamp) {
                consoleMessage = this.appendTimestamp(consoleMessage);
            }
            // If we should add the level
            if (this.config.console.appendLevel) {
                /** Color and style the traces */
                const color = this.getConsoleColor(level);
                /** Trace literal we transfrom the ENUM to str */
                const strLvl = this.getLevelLiteral(level);
                console[lvl](`%c${strLvl}`, color, consoleMessage, ...params);
            }
            else {
                console[lvl](consoleMessage, ...params);
            }
        }
    }
    /**
     * Log to the message to the server
     */
    logToServer(level, msg, ...params) {
        if (this.config.server && level >= this.config.server.level) {
            // Error objects cannot be simply serialized by JSON.stringify, which leads to '{}' being sent to the backend.
            // As a workaround Error objects are serialized here.
            const serializedParams = [];
            serializedParams.push(...params.map((entry) => {
                if (entry && entry.stack && entry.message) {
                    try {
                        // This turns the Error into an object:
                        const errorObject = serializeError(entry);
                        // Typical Angular Errors contain a lot of additional data that is not useful
                        // for investigations. And it makes the transferred data very large: ~15-50kb
                        // To decrease that a bit, remove certain attributes:
                        delete errorObject['zone'];
                        delete errorObject['task'];
                        delete errorObject['promise'];
                        return JSON.stringify(errorObject);
                    }
                    catch (error) {
                        serializedParams.push(`Serialization of the error failed. Original Error Message: ${entry?.message}`);
                        serializedParams.push(`Serialization error message: ${error?.message}`);
                        return entry;
                    }
                }
                return entry;
            }));
            // Add some more metadata:
            serializedParams.push({ useragent: window?.navigator?.userAgent });
            const body = {
                level: this.getLevelLiteral(level),
                app_name: this.config.server.appName,
                'session-id': this.ngxLoggerSessionIdService.getOrCreateSessionId(),
                message: msg,
                params: serializedParams,
            };
            this.http.post(this.config.server.endpoint, body).subscribe(() => { }, 
            // If there is an error logging to server, lets try to log in console.
            (error) => {
                this.logToConsole(NGX_LOGGER_LEVELS.ERROR, error.message);
            });
        }
    }
    /**
     * In base of the level get the console.XXX output
     * @param level Trace level
     * @returns STR of the console to execute
     */
    getConsoleMethodForLogLevel(level) {
        switch (level) {
            case NGX_LOGGER_LEVELS.DEBUG:
                return 'log';
            case NGX_LOGGER_LEVELS.INFO:
                return 'info';
            case NGX_LOGGER_LEVELS.WARN:
                return 'warn';
            case NGX_LOGGER_LEVELS.ERROR:
                return 'error';
        }
    }
    /**
     * We get the styles to add to the trace
     * @param level Level of the trace
     * @returns Styles
     */
    getConsoleColor(level) {
        const base = BASE_STYLE.join(';');
        const colors = {
            [NGX_LOGGER_LEVELS.DEBUG]: '#08b508',
            [NGX_LOGGER_LEVELS.INFO]: '#035ff7',
            [NGX_LOGGER_LEVELS.WARN]: '#cec416',
            [NGX_LOGGER_LEVELS.ERROR]: '#f70000',
        };
        return base.replace(TO_REPLACE, colors[level]);
    }
    /**
     * In base to the literal return the trace literal
     * @param level Level of the trace
     * @returns Literal of the trace
     */
    getLevelLiteral(level) {
        const LITERALS = [
            NGX_LITERAL_LOGGER_LEVELS.DEBUG,
            NGX_LITERAL_LOGGER_LEVELS.INFO,
            NGX_LITERAL_LOGGER_LEVELS.WARN,
            NGX_LITERAL_LOGGER_LEVELS.ERROR,
        ];
        return LITERALS[level];
    }
    /**
     * Append the TS to the current message
     * @param msg The current message
     * @return The message with the TS appended
     */
    appendTimestamp(msg) {
        let date = new Date().toISOString();
        date = date.substr(date.indexOf('T') + 1).replace('Z', '');
        return `${date} - ${msg}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerService, deps: [{ token: NGX_LOGGER_CONFIG, optional: true }, { token: i1.HttpClient, optional: true }, { token: NgxLoggerSessionIdService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [NGX_LOGGER_CONFIG]
                }] }, { type: i1.HttpClient, decorators: [{
                    type: Optional
                }] }, { type: NgxLoggerSessionIdService }] });

class NgxLoggerOnerrorService {
    constructor(logger, document) {
        this.logger = logger;
        this.document = document;
        this.document.onerror = (message, source, lineno, colno, error) => {
            try {
                this.logger.errorToServer(`Document.OnError: ` + message, {
                    source: source,
                    lineNumber: lineno,
                    colNumber: colno,
                    error: error
                });
            }
            catch (err) {
                console.error('Error Trying to send the error to the server', err);
            }
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerOnerrorService, deps: [{ token: NgxLoggerService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerOnerrorService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerOnerrorService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: NgxLoggerService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class NgxLoggerGlobalErrorModule {
    // Putting the service here, will ensure that it is instantiated when the module is imported
    constructor(ngxLoggerOnerrorService) {
        this.ngxLoggerOnerrorService = ngxLoggerOnerrorService;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerGlobalErrorModule, deps: [{ token: NgxLoggerOnerrorService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerGlobalErrorModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerGlobalErrorModule, providers: [
            NgxLoggerOnerrorService
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerGlobalErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        NgxLoggerOnerrorService
                    ]
                }]
        }], ctorParameters: () => [{ type: NgxLoggerOnerrorService }] });

/**
 * We get send on the header the session-id so the BFF can add to the logs on Kibana and we can identify what calls
 * belongs to a given session.
 */
class NgxLoggerInterceptor {
    constructor(ngxLoggerSessionIdService) {
        this.ngxLoggerSessionIdService = ngxLoggerSessionIdService;
    }
    intercept(req, next) {
        const reqClone = req.clone({
            setHeaders: {
                'session-id': this.ngxLoggerSessionIdService.getOrCreateSessionId()
            }
        });
        return next.handle(reqClone);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerInterceptor, deps: [{ token: NgxLoggerSessionIdService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.3", ngImport: i0, type: NgxLoggerInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: NgxLoggerSessionIdService }] });

class NgxLoggerErrorHandler {
    constructor(logger) {
        this.logger = logger;
    }
    handleError(error) {
        this.logger.errorToServer('NgxLoggerErrorHandler: Unhandled Error: ', error);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { NGX_LITERAL_LOGGER_LEVELS, NGX_LOGGER_CONFIG, NGX_LOGGER_LEVELS, NgxLoggerErrorHandler, NgxLoggerGlobalErrorModule, NgxLoggerInterceptor, NgxLoggerService, NgxLoggerSessionIdService, STORAGE_SESSION_ID_KEY };
